import GuardianClient from "@common/api/GuardianClient";
import ComplianceClient from "@common/api/ComplianceClient";
import { handleError } from "@common/components/helpers/error";

export const setKiosk = (data) => async (dispatch) => {
  dispatch({ type: "KIOSK_VIEW", payload: data });
};

export const setSection = (section) => async (dispatch) => {
  dispatch({ type: "COMPLIANCE_SUMMARY_TAB", payload: { section } });
};

export const fetchComplianceMetadata = () => async (dispatch) => {
  try {
    const response = await GuardianClient.get(`Configuration/Metadata`);
    dispatch({ type: "FETCH_COMPLIANCE_METADATA", payload: response });
  } catch (error) {
    handleError(error);
  }
};

export const clearComplianceMetadata = () => (dispatch) => {
  dispatch({ type: "FETCH_COMPLIANCE_METADATA", payload: [] });
};
export const clearComplianceRecords = () => (dispatch) => {
  dispatch({ type: "FETCH_COMPLIANCE_REC", payload: { data: [] } });
};

export const fetchComplianceSummaryMetadata = () => async (dispatch) => {
  await ComplianceClient.getMetaData()
    .then((response) => {
      dispatch({
        type: "COMPLIANCE_SUMMARY_TAB",
        payload: { filters: { ...response } },
      });
    })
    .catch(handleError)
    .finally(() =>
      dispatch({
        type: "COMPLIANCE_SUMMARY_TAB",
        payload: { filtersLoading: false },
      }),
    );
};

export const fetchComplianceModel = (activeModule) => async (dispatch) => {
  try {
    const response = await GuardianClient.get(`${activeModule}/Create`);
    dispatch({ type: "FETCH_COMPLIANCE_MODEL", payload: response });
  } catch (error) {
    handleError(error);
  }
};

export const fetchComplianceRecords = (activeModule, options = {}) => async (dispatch) => {
  try {
    if (activeModule === "ReviewQuestion") {
      const reviewTypeId = options.reviewTypeId ?? 2;
      const response = await GuardianClient.post(`${activeModule}/Search`, {
        reviewTypeId,
      });
      const data = response.data;
      dispatch({ type: "FETCH_COMPLIANCE_REC", payload: { data } });
      return;
    }
    const response = await GuardianClient.post(`${activeModule}/Search`, {});
    const data = await Promise.all(
      response.data.map(async (data) => {
        const { name, ...rest } = await GuardianClient.get(
          `${activeModule}/Read?id=${data.id}`,
        );
        return name ? { name, ...rest } : rest;
      }),
    );
    dispatch({ type: "FETCH_COMPLIANCE_REC", payload: { data } });
  } catch (error) {
    handleError(error);
  }
};

export const updateCompliance = async (activeModule, data) => {
  try {
    const originData = await GuardianClient.get(
      `${activeModule}/Read?id=${data.id}`,
    );

    const payload = {
      ...originData,
      ...data,
      ...data.riskScore && !isNaN(data.riskScore)? { riskScore: parseInt(data.riskScore) } : {}
    }

    const response = await GuardianClient.post(`${activeModule}/Update`, payload);
    return response;
  } catch (error) {
    handleError(error);
  }
};

export const deleteCompliance = async (activeModule, id) => {
  try {
    const response = await GuardianClient.get(
      `${activeModule}/Delete?id=${id}`,
      {},
    );
    return response;
  } catch (error) {
    handleError(error);
  }
};

export const createCompliance = async (activeModule, data) => {
  try {
    const response = await GuardianClient.post(`${activeModule}/Create`, data);
    return response;
  } catch (error) {
    handleError(error);
  }
};

export const getSelectedModuleData = async (activeModule, id) => {
  try {
    const response = await GuardianClient.get(`${activeModule}/Read?id=${id}`);
    return response;
  } catch (error) {
    handleError(error);
  }
};

export const getReviewQuestionCreateTemplate = async () => {
  try {
    const response = await GuardianClient.get("ReviewQuestion/Create");
    return response;
  } catch (error) {
    handleError(error);
  }
};

export const createReviewQuestion = async (record) => {
  try {
    const payload = { ...record, reviewTypeId: record.reviewTypeId ?? 2 };
    const response = await GuardianClient.post("ReviewQuestion/Create", payload);
    return response;
  } catch (error) {
    handleError(error);
  }
};

export const fetchCustomerConfig = () => async (dispatch) => {
  try {
    const response = await GuardianClient.get(`CustomerConfiguration/Read`);

    const { fieldRequiredTypes } = await GuardianClient.get(
      `CustomerConfiguration/Update`,
    );
    dispatch({
      type: "FETCH_COMPLIANCE_CUSTOMER_CONFIG",
      payload: { ...response, fieldRequiredTypes },
    });
  } catch (error) {
    handleError(error);
  }
};

export const updateCustomerConfig = (data) => async (dispatch) => {
  try {
    const response = await GuardianClient.post(
      `CustomerConfiguration/Update`,
      data,
    );

    dispatch({ type: "FETCH_COMPLIANCE_CUSTOMER_CONFIG", payload: response });
  } catch (error) {
    handleError(error);
  }
};

export const fetchAlertConfig = () => async (dispatch) => {
  try {
    const response = await GuardianClient.get(`AlertConfiguration/Read`);

    const { fieldRequiredTypes } = await GuardianClient.get(
      `AlertConfiguration/Update`,
    );
    dispatch({
      type: "FETCH_COMPLIANCE_ALERT_CONFIG",
      payload: { ...response, fieldRequiredTypes },
    });
  } catch (error) {
    handleError(error);
  }
};

export const updateAlertConfig = (data) => async (dispatch) => {
  try {
    const response = await GuardianClient.post(
      `AlertConfiguration/Update`,
      data,
    );

    dispatch({ type: "FETCH_COMPLIANCE_ALERT_CONFIG", payload: response });
  } catch (error) {
    handleError(error);
  }
};

export const fetchAgeConfig = () => async (dispatch) => {
  try {
    const response = await GuardianClient.get(
      `AgeVerificationConfiguration/Read`,
    );
    dispatch({ type: "FETCH_COMPLIANCE_AGE_CONFIG", payload: response });
  } catch (error) {
    handleError(error);
  }
};

export const updateAgeConfig = (data) => async (dispatch) => {
  try {
    const response = await GuardianClient.post(
      `AgeVerificationConfiguration/Update`,
      data,
    );

    dispatch({ type: "FETCH_COMPLIANCE_AGE_CONFIG", payload: response });
  } catch (error) {
    handleError(error);
  }
};

export const fetchCustomerInteractionConfig = () => async (dispatch) => {
  try {
    const response = await GuardianClient.getCustomerInteractionConfig();
    dispatch({ type: "FETCH_COMPLIANCE_CUSTOMER_INTERACTION_CONFIG", payload: response });
  } catch (error) {
    handleError(error);
  }
};

export const updateCustomerInteractionConfig = (setTemporaryBreak, setVulnerable) => async (dispatch) => {
  try {
    const forUpdate = await GuardianClient.getCustomerInteractionConfigForUpdate();
    const rowVersion = forUpdate?.record?.rowVersion;
    const id = forUpdate?.record?.id;
    const record = { 
      "hasSetTemporaryBreak": setTemporaryBreak,
      "hasSetVulnerable": setVulnerable,
      rowVersion,
      id,
    };
    
    const response = await GuardianClient.updateCustomerInteractionConfig(record);
    dispatch({ type: "FETCH_COMPLIANCE_CUSTOMER_INTERACTION_CONFIG", payload: response });
    return response;
  } catch (error) {
    handleError(error);
    throw error;
  }
};
